import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import React, { useEffect, useRef, useState } from "react";
import { useConsumerSearch } from "../../hooks";
import { ConsumerListItem } from "./ConsumerList";
import { ConsumerSearchInput } from "./SearchConsumer";

interface ConsumerLookupProps {
  onConsumerSelect: (consumer: any) => void;
  consumerNumber?: string;
  source?: string | null;
}

export const ConsumerLookup: React.FC<ConsumerLookupProps> = ({
  onConsumerSelect,
  consumerNumber,
  source,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const autoSelectionDoneRef = useRef(false);
  const previousConsumerNumberRef = useRef<string | undefined>(undefined);
  const manuallyRemovedRef = useRef(false);

  const { data: consumerList } = useConsumerSearch({
    searchData: debouncedSearchTerm,
    remoteUtilityId: getRemoteUtilityId(),
  });

  // Auto-populate search term when consumerNumber is provided or changes
  useEffect(() => {
    // Check if consumerNumber has changed
    if (
      consumerNumber &&
      consumerNumber !== previousConsumerNumberRef.current
    ) {
      // Reset auto-selection flag when consumer number changes
      autoSelectionDoneRef.current = false;
      manuallyRemovedRef.current = false;
      previousConsumerNumberRef.current = consumerNumber;

      // Set both search terms immediately
      setSearchTerm(consumerNumber);
      setDebouncedSearchTerm(consumerNumber);
    }
  }, [consumerNumber]);

  // Debounce search term for user typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Auto-select consumer when search results are available
  useEffect(() => {
    if (
      consumerNumber &&
      !autoSelectionDoneRef.current &&
      !manuallyRemovedRef.current &&
      consumerList &&
      consumerList.length > 0
    ) {
      const matchingConsumer = consumerList.find(
        (consumer) => consumer.consumerNo === consumerNumber,
      );

      if (matchingConsumer) {
        onConsumerSelect(matchingConsumer);
        autoSelectionDoneRef.current = true;
      }
    }
  }, [consumerList, consumerNumber, onConsumerSelect]);

  const handleConsumerClick = (consumer: any) => {
    onConsumerSelect(consumer);
    // Mark as manually selected to prevent auto-reselection
    autoSelectionDoneRef.current = true;
  };

  return (
    <div>
      <div className="mb-4">
        <ConsumerSearchInput
          onSearchChange={setSearchTerm}
          placeholder="Search consumers by name or number..."
          value={searchTerm}
          disabled={source === "account-management"}
        />
      </div>

      {consumerList?.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="max-h-[320px] overflow-y-auto">
            {consumerList?.map((consumer) => (
              <ConsumerListItem
                key={consumer.id}
                consumer={consumer}
                onClick={handleConsumerClick}
              />
            ))}
          </div>
        </div>
      )}

      {debouncedSearchTerm.trim() && consumerList?.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <p className="text-gray-500">
            No consumers found matching "{debouncedSearchTerm}"
          </p>
        </div>
      )}
    </div>
  );
};
